import { Component, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { AngularModule } from '../../../modules/angular.module';
import { ZorroModule } from '../../../modules/zorro.module';
import { DragHelper } from '../../../utils/drag.helper';
import { v4 } from 'uuid';
import { NamedBlob } from '@mira-avant/mtlibs';
import { FileHelper } from '../../../utils/file.helper';

export interface DropzoneFile {
	fileName: string;
	mimeType: string;
	size: number;
	data: Blob;
}

@Component({
	selector: 'app-dropzone',
	imports: [AngularModule, ZorroModule],
	templateUrl: './dropzone.html',
	styleUrl: './dropzone.scss',
})
export class Dropzone {
	@ViewChild('uploadInput', { static: true }) uploadInput!: ElementRef<HTMLInputElement>;
	public pick = input<string>('files'); // or "directory"
	public accept = input<string>('.zip,.h5p,application/zip,application/x-zip-compressed,application/h5p');
	public multiple = input<boolean>(false);
	public directory = input<boolean>(false);
	public onCompleted = output<DropzoneFile[]>();
	public isHovering = signal(false);
	public uploadId: string = 'upload-' + v4();

	public onDrag(event: DragEvent): void {
		this.isHovering.set(event.type == 'dragenter');
	}

	public onDrop(event: DragEvent): void {
		event.preventDefault();
		this.isHovering.set(false);
		DragHelper.getDroppedFiles$(event).subscribe(
			(files: any[]) => {
				const fileList = {
					length: files.length,
					item: (index: number) => files[index],
					[Symbol.iterator]: function* () {
						yield* files;
					},
				} as FileList;
				this.handleFileSelection(fileList);
			},
			error => this.handleError(error)
		);
	}

	public onClick(event: Event): void {
		this.setDirectoryUpload(this.pick() === 'directory');
	}

	public onChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files) {
			this.handleFileSelection(input.files);
		}
	}

	private handleFileSelection(files: FileList): void {
		const allowedFiles = this.getAllowedFiles(files);
		if (allowedFiles.length) {
			const dropzoneFiles: DropzoneFile[] = allowedFiles.map(file => ({
				fileName: file.name,
				mimeType: file.type,
				size: file.size,
				data: file,
			}));
			this.onCompleted.emit(dropzoneFiles);
			this.reset();
		} else if (this.accept()) {
			this.handleError(new Error(`Please select an allowed file type: ${this.allowedFileTypes}`));
		}
	}

	private handleError(error: Error): void {
		// this.handleUploadComplete(false, error.message);
		console.error('Dropzone error:', error);
		this.reset();
	}

	private reset(): void {
		if (this.uploadInput.nativeElement) {
			this.uploadInput.nativeElement.value = '';
		}
	}

	private getAllowedFiles(fileList: FileList | File[] | NamedBlob[]): (File | NamedBlob)[] {
		const files = fileList instanceof FileList ? Array.from(fileList) : fileList;

		if (this.accept) {
			const acceptedTypes: string[] = this.accept()
				.split(',')
				.map(type => type.trim());

			const acceptedFiles: (File | NamedBlob)[] = [];
			const rejectedFiles: (File | NamedBlob)[] = [];

			for (const file of files) {
				if (file.name.startsWith('.')) {
					rejectedFiles.push(file);
					continue;
				}

				const fileExtension = '.' + file.name.split('.').pop()!.toLowerCase();
				const fileMimeType = file.type.toLowerCase();
				const isAccepted = acceptedTypes.some(type => {
					if (type.startsWith('.')) {
						return type.toLowerCase() === fileExtension;
					} else {
						const [acceptedType, acceptedSubtype] = type.split('/');
						const [fileType, fileSubtype] = fileMimeType.split('/');
						return (
							acceptedType === fileType && (acceptedSubtype === '*' || acceptedSubtype === fileSubtype)
						);
					}
				});

				if (isAccepted) {
					acceptedFiles.push(file);
				} else {
					rejectedFiles.push(file);
				}
			}

			if (rejectedFiles.length > 0) {
				this.showRejectedFilesAlert(rejectedFiles);
			}
			return acceptedFiles;
		} else {
			return files;
		}
	}

	private showRejectedFilesAlert(rejectedFiles: (File | NamedBlob)[]): void {
		const inferredFileExtension: string = this.accept()
			.split(',')
			.map(type => FileHelper.getMimetypeExt(type.trim()).toUpperCase())
			.filter(ext => ext !== '') // Replace _.compact
			.join(' or ');
		const messagePart1: string = `The following file${rejectedFiles.length !== 1 ? 's' : ''} could not be uploaded:`;
		const messagePart2: string = rejectedFiles.map(file => file.name).join('<br />'); // Replace _.map
		const messagePart3: string = inferredFileExtension
			? `Please ensure your file type is ${inferredFileExtension} and try again.`
			: `File must have mimeType ${this.accept()}:`;
		alert(`Incorrect File Format. ${messagePart1} ${messagePart2} ${messagePart3}`);
	}

	private setDirectoryUpload(bool: boolean = this.directory() && this.multiple()): void {
		if (bool) {
			this.uploadInput.nativeElement.setAttribute('webkitdirectory', 'true');
		} else {
			this.uploadInput.nativeElement.removeAttribute('webkitdirectory');
		}
	}

	public get allowedFileTypes(): string {
		return this.accept()
			.split(',')
			.map(type => {
				let segments = type.split('/');
				return segments[1] && segments[1] != '*' ? '.' + segments[1] : segments[0];
			})
			.join(', ');
	}
}
